import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ActionDialog, type ActionRequest } from "@/components/crm/ActionDialog";
import { EditDialog } from "@/components/crm/EditDialog";
import { SearchDialog } from "@/components/crm/SearchDialog";
import { useCrm } from "@/hooks/useCrm";
import { getAction, initialValues, type FormValue, type Matches } from "@/lib/actions";
import { ApiError } from "@/lib/api";
import type { ActionContext, ActionResult, TableKey } from "@/lib/types";

// De vensters van de werkschermen (vroeger app.js): het actievenster, Bewerken en Zoeken, plus de
// matchresultaten per woonvraag die het actievenster en de shortlist delen. Elk scherm opent ze
// met useWorkspace(); er is telkens maar één venster tegelijk open.

export interface Workspace {
  /** Een actie starten: het venster met het formulier, of meteen uitvoeren bij een snelle actie. */
  openAction: (actionId: string, context?: Partial<ActionContext>, onDone?: (result: ActionResult) => void, preset?: Record<string, FormValue>) => void;
  /** Bewerken (§6.4): `id` = null maakt een nieuw record. */
  openEdit: (table: TableKey, id: string | null) => void;
  openSearch: () => void;
  matches: Matches;
}

const WorkspaceContext = createContext<Workspace | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const crm = useCrm();
  const navigate = useNavigate();
  const [action, setAction] = useState<ActionRequest | null>(null);
  const [edit, setEdit] = useState<{ table: TableKey; id: string | null; key: number } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [matches, setMatches] = useState<Matches>({});

  /** Na een geslaagde actie: melding, matchresultaten bewaren, en een knop naar een nieuw pand of bedrijf. */
  const finish = useCallback(
    (actionId: string, result: ActionResult, onDone?: (result: ActionResult) => void) => {
      const def = getAction(actionId);
      if (result.extra.matches && result.context.requestId) setMatches((m) => ({ ...m, [result.context.requestId!]: result.extra.matches! }));
      const created = result.records.find((r) => r.operation === "create" && (r.table === "properties" || r.table === "companies"));
      toast.success(def?.label ?? actionId, {
        description: result.summary,
        action: created ? { label: created.table === "properties" ? "Pandfiche openen" : "Bedrijf openen", onClick: () => navigate(`/${created.table === "properties" ? "panden" : "bedrijven"}/${created.record.id}`) } : undefined
      });
      onDone?.(result);
    },
    [navigate]
  );

  const openAction = useCallback<Workspace["openAction"]>(
    (actionId, context = {}, onDone, preset) => {
      const def = getAction(actionId);
      if (!def) {
        toast.error("Onbekende actie", { description: actionId });
        return;
      }
      if (!def.quick) {
        setAction({ actionId, context, onDone, preset, key: Date.now() });
        return;
      }
      crm
        .runAction(actionId, initialValues(def, crm, context, matches, preset), context)
        .then((result) => finish(actionId, result, onDone))
        .catch((error: Error) => toast.error(def.label, { description: error instanceof ApiError ? error.message : String(error) }));
    },
    [crm, finish, matches]
  );

  // Zoeken met Ctrl+K of Cmd+K, overal in de app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo<Workspace>(
    () => ({ openAction, openEdit: (table, id) => setEdit({ table, id, key: Date.now() }), openSearch: () => setSearchOpen(true), matches }),
    [openAction, matches]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
      {action && <ActionDialog key={action.key} request={action} matches={matches} onClose={() => setAction(null)} onSuccess={(result) => finish(action.actionId, result, action.onDone)} />}
      {edit && <EditDialog key={edit.key} table={edit.table} id={edit.id} onClose={() => setEdit(null)} />}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} onAction={(id) => openAction(id)} />
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): Workspace {
  const workspace = useContext(WorkspaceContext);
  if (!workspace) throw new Error("useWorkspace buiten WorkspaceProvider");
  return workspace;
}
